import PrivateLayout from "@/components/private/PrivateLayout";
import { ADMIN_NAV } from "@/components/private/adminNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { ArrowRight, Plus, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";

export default function AdminPlayers() {
  const { data: players, refetch, isLoading } = trpc.players.listAdmin.useQuery();
  const createMut = trpc.players.create.useMutation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    level: "",
    handicap: "",
    objective: "",
  });

  const handleCreate = async () => {
    if (!form.fullName.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      await createMut.mutateAsync({
        fullName: form.fullName.trim(),
        email: form.email || undefined,
        phone: form.phone || undefined,
        level: form.level || undefined,
        handicap: form.handicap || undefined,
        objective: form.objective || undefined,
      });
      toast.success("Jugador creado");
      setOpen(false);
      setForm({ fullName: "", email: "", phone: "", level: "", handicap: "", objective: "" });
      refetch();
    } catch (e: any) {
      toast.error(e.message || "Error al crear jugador");
    }
  };

  return (
    <PrivateLayout title="Administración" nav={ADMIN_NAV} requireRole="admin">
      <div className="p-6 md:p-12 max-w-6xl">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <h1 className="font-display font-light text-[var(--forest-deep)] leading-[1.05]" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
              Jugadores
            </h1>
            <p className="mt-2 text-sm text-[var(--forest-deep)]/70">
              Gestiona todas las fichas clínicas y sus informes.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[var(--forest)] hover:bg-[var(--forest-deep)] text-[var(--cream)] gap-2">
                <Plus size={16} /> Nuevo jugador
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nueva ficha de jugador</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                <div className="md:col-span-2">
                  <Label htmlFor="fullName">Nombre completo *</Label>
                  <Input id="fullName" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="level">Nivel</Label>
                  <Input id="level" placeholder="Amateur medio" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="handicap">Hándicap</Label>
                  <Input id="handicap" placeholder="8.2" value={form.handicap} onChange={(e) => setForm({ ...form, handicap: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="objective">Objetivo</Label>
                  <Textarea id="objective" rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={handleCreate} disabled={createMut.isPending} className="bg-[var(--forest)] text-[var(--cream)]">
                  {createMut.isPending ? "Creando..." : "Crear jugador"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="h-96 rounded-xl bg-white/50 animate-pulse" />
        ) : !players || players.length === 0 ? (
          <Card className="p-16 text-center bg-white border border-[var(--forest-deep)]/10">
            <User className="mx-auto text-[var(--forest)]/30 mb-4" size={36} />
            <h2 className="font-display text-2xl font-medium text-[var(--forest-deep)]">Aún no hay jugadores</h2>
            <p className="mt-2 text-[var(--forest-deep)]/70">Crea tu primer jugador con el botón superior.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {players.map((p) => (
              <Link
                key={p.id}
                href={`/admin/jugadores/${p.id}`}
                className="block p-5 rounded-xl bg-white border border-[var(--forest-deep)]/10 hover:border-[var(--forest)]/40 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-[var(--forest)]/10 flex items-center justify-center font-display text-xl text-[var(--forest)]">
                    {p.fullName.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[var(--forest-deep)] truncate">{p.fullName}</div>
                    <div className="text-xs text-[var(--forest-deep)]/60 truncate">
                      {p.level || "Sin nivel"} · HCP {p.handicap || "—"} · {p.email || "Sin email"}
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-[var(--forest)]/50 shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PrivateLayout>
  );
}
